import { type ChatInputRef } from "@bonusx-global/bx-design-system";
import * as Sentry from "@sentry/react";
import Drawer from "@mui/material/Drawer";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import isEmpty from "lodash/isEmpty";
import isFunction from "lodash/isFunction";
import last from "lodash/last";
import { useCallback, useEffect, useRef } from "react";
import { useTrackClaraConversationStarted } from "../../../features/ai-chatbot/hooks/useTrackClaraConversationStarted";
import useIsMobile from "../../../hooks/device/useIsMobile";
import { useDialog } from "../../hooks/useDialog";
import { BonusXThemeProvider } from "../../theme/BonusXThemeProvider";
import { DEFAULT_ANIMATION_TIME } from "./chatbot.const";
import { SenderTypeEnum } from "./enums/sender-type.enum";
import { useChatbotContent } from "./hooks/use-chatbot-content/use-chatbot-content";
import { useChatbotContext } from "./hooks/use-chatbot-context/use-chatbot-context";
import { useChatbotDrawerProps } from "./hooks/use-chatbot-drawer-props/use-chatbot-drawer-props";
import { useChatbotMediator } from "./hooks/use-chatbot-manager/use-chatbot-mediator";
import { useChatbotPanel } from "./hooks/use-chatbot-panel/use-chatbot-panel";

export function Chatbot(): JSX.Element {
  const { isChatbotOpen, openChatbotPanel, closeChatbotPanel } = useChatbotPanel();
  const isMobile = useIsMobile();
  const chatInputRef = useRef<ChatInputRef | null>(null);
  const { state } = useChatbotContext();
  const chatbotMediator = useChatbotMediator();
  const { trackConversationStarted } = useTrackClaraConversationStarted();
  const MLDialog = useDialog();

  const hasBeenOpenedRef = useRef(isChatbotOpen);
  if (isChatbotOpen) {
    hasBeenOpenedRef.current = true;
  }

  const isChatDisabled = chatbotMediator.isChatDisabled(state, isChatbotOpen);

  const focusChatInput = useCallback(
    function focusChatInput() {
      if (!chatInputRef.current || !isFunction(chatInputRef.current.focus) || isChatDisabled) {
        return;
      }

      setTimeout(function focusChatInputAfterDelay() {
        chatInputRef.current?.focus();

        setTimeout(function scrollToBottomAfterChatInputFocus() {
          chatbotMediator.setScrollToBottomAction(true);
        }, DEFAULT_ANIMATION_TIME);
      }, DEFAULT_ANIMATION_TIME);
    },
    [chatbotMediator, isChatDisabled]
  );

  const handleClose = useCallback(() => {
    closeChatbotPanel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpen = useCallback(() => {
    openChatbotPanel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = useCallback(
    async function sendToAIService(messageText: string) {
      const isConversationStart = isEmpty(state.messages);
      await chatbotMediator.sendToAIService({ message: messageText });

      if (isConversationStart) {
        await trackConversationStarted();
      }
    },
    [chatbotMediator, state.messages, trackConversationStarted]
  );

  const handleOnRetryCallback = useCallback(
    async function handleOnRetry() {
      const userMessages = state.messages.filter(function filterUserMessages(message) {
        return message.senderType === SenderTypeEnum.USER;
      });
      const lastMessage = last(userMessages);

      if (!lastMessage) {
        return;
      }

      await chatbotMediator.retryLastMessage({ message: lastMessage.text });
    },
    [chatbotMediator, state.messages]
  );

  const handleOnChange = useCallback(
    function handleTextInputOnChange(event: React.ChangeEvent<HTMLInputElement>) {
      chatbotMediator.toggleEmptyState({
        currentInputLength: event?.target?.value?.length,
        currentMessagesLength: state?.messages?.length,
        isCurrentlyEmptyState: state?.showEmptyState,
      });
    },
    [chatbotMediator, state?.messages?.length, state?.showEmptyState]
  );

  const handleScrollToBottomClick = useCallback(
    function handleScrollToBottomClick() {
      chatbotMediator.setScrollToBottomAction(true);
    },
    [chatbotMediator]
  );

  const handleLastChatButtonClick = useCallback(
    async function handleLastChatButtonClick() {
      try {
        const isNewSessionCreated = await chatbotMediator.cloneFromPreviousChat();
        if (!isNewSessionCreated) {
          MLDialog.showSnackbar("Errore nella riproduzione della precedente sessione. Riprova più tardi.", {
            variant: "error",
          });
        }
      } catch (error) {
        MLDialog.showSnackbar("Errore nella riproduzione della precedente sessione. Riprova più tardi.", {
          variant: "error",
        });
      }
    },
    [chatbotMediator, MLDialog]
  );

  const handleNewSession = useCallback(
    async function handleNewSessionButtonClick() {
      try {
        const isNewSessionCreated = await chatbotMediator.createNewSession();
        if (!isNewSessionCreated) {
          MLDialog.showSnackbar("Errore nella creazione della nuova sessione. Riprova più tardi.", {
            variant: "error",
          });
        } else {
          await chatbotMediator.canCloneFromPreviousChat().catch(function handleCanCloneError(error) {
            console.warn("Failed to check previous chat clone availability:", error);
          });
        }
      } catch (error) {
        MLDialog.showSnackbar("Errore nella creazione della nuova sessione. Riprova più tardi.", {
          variant: "error",
        });
      }
    },
    [chatbotMediator, MLDialog]
  );

  useEffect(
    function focusChatInputOnAiResponse() {
      if (isMobile) {
        return;
      }

      const isAiResponse =
        state.messages.length > 0 && state.messages[state.messages.length - 1].senderType === SenderTypeEnum.ASSISTANT;
      if (!isAiResponse) {
        return;
      }

      focusChatInput();
    },
    [focusChatInput, isMobile, state.messages]
  );

  useEffect(
    function focusChatInputOnDrawerOpen() {
      if (!isChatbotOpen) {
        return;
      }
      focusChatInput();
    },
    [focusChatInput, isChatbotOpen]
  );

  useEffect(
    function onChatBotOpen() {
      if (!isChatbotOpen) {
        return;
      }
      chatbotMediator.canCloneFromPreviousChat().catch(function handleCanCloneError(error) {
        Sentry.addBreadcrumb({
          category: "chatbot",
          message: "Failed to check previous chat clone availability",
          level: "warning",
        });
        Sentry.captureException(error);
      });
      chatbotMediator.checkRequiredSubscriptionForChatbot().catch(function handleSubscriptionCheckError(error) {
        Sentry.addBreadcrumb({
          category: "chatbot",
          message: "Failed to check subscription for chatbot",
          level: "warning",
        });
        Sentry.captureException(error);
      });
    },
    [isChatbotOpen]
  );

  const drawerProps = useChatbotDrawerProps({
    isMobile,
    isChatbotOpen,
    handleOpen,
    handleClose,
  });

  const ChatContent = useChatbotContent({
    isMobile,
    messages: state.messages,
    showEmptyState: state.showEmptyState,
    chatInputRef,
    handleSubmit,
    handleClose,
    handleOnChange,
    handleScrollToBottomClick,
    handleNewSession,
    handleLastChatButtonClick,
    handleOnRetry: handleOnRetryCallback,
    disabled: isChatDisabled,
    isSending: state.isSending,
    showScrollToBottomButton: state.showScrollToBottom,
    isChatbotAvailableForCurrentUser: state.isAvailableInCurrentSubscription,
    hasLastChatButton: state.canCloneFromPreviousChat,
    errorWhileGeneratingResponse: state.errorWhileGeneratingResponse,
    errorMessage: state.errorMessage,
    requiredTierForChatbot: state.requiredTierForChatbot,
    showRetryButton: state.showRetryButton,
    retryButtonText: state.retryButtonText,
  });

  const DrawerComponent = isMobile ? SwipeableDrawer : Drawer;

  return (
    <BonusXThemeProvider>
      <DrawerComponent {...drawerProps}>{hasBeenOpenedRef.current ? ChatContent : null}</DrawerComponent>
    </BonusXThemeProvider>
  );
}
